import { Children, cloneElement, isValidElement } from 'react';
import { useTranslation } from 'react-i18next';
import { InfoCircleOutlined, TeamOutlined } from '@ant-design/icons';
import { InputNumber, Segmented, Select, Spin, Switch, Tooltip } from 'antd';
import { CurrencyBadge } from '@/components/currency-badge';
import { cn } from '@/libs/utils';

// Setting cards for the profile tiles (HR, time tracking, e-invoices…) and
// the document settings. A field is <Form.Item name noStyle><SettingX …/></Form.Item>, so
// names, values and saving stay those of the form.

const Hint = ({ text }) =>
  text ? (
    <Tooltip title={text}>
      <InfoCircleOutlined className="ml-1 text-xs text-zinc-400" />
    </Tooltip>
  ) : null;

/**
 * A label with a qualifier in brackets ("… (po Zakonu o radu)") puts the
 * qualifier on its own muted line, so the label does not break mid-phrase.
 */
function Title({ text, hint }) {
  const match = /^(.*?)\s*(\(.*\))\s*$/.exec(text || '');
  const main = match ? match[1] : text;
  return (
    <>
      {main}
      <Hint text={hint} />
      {match && (
        <span className="block text-[11.5px] font-normal text-zinc-500">
          {match[2]}
        </span>
      )}
    </>
  );
}

/** A setting card; in edit mode the whole card is the switch. */
export function SettingSwitch({
  value,
  onChange,
  locked,
  icon,
  title,
  desc,
  hint,
  wide,
  children,
}) {
  const { t } = useTranslation('forms');
  const on = !!value;
  const toggle = () => !locked && onChange?.(!on);

  return (
    <div
      className={cn(
        'setting-card rounded-xl border transition-colors',
        on
          ? 'border-[rgba(var(--accentColor),0.35)] bg-[rgba(var(--accentColor),0.06)]'
          : 'border-zinc-200 bg-white',
        wide && 'col-span-2'
      )}
    >
      <div
        role="switch"
        aria-checked={on}
        tabIndex={locked ? -1 : 0}
        onClick={toggle}
        onKeyDown={(e) => (e.key === ' ' || e.key === 'Enter') && toggle()}
        className={cn(
          'flex items-start gap-2.5 p-2.5',
          !locked && 'cursor-pointer'
        )}
      >
        <span
          className={cn(
            'grid h-8 w-8 shrink-0 place-items-center rounded-lg text-[15px]',
            on
              ? 'bg-[rgba(var(--accentColor),0.14)] text-accent'
              : 'bg-zinc-100 text-zinc-500'
          )}
        >
          {icon}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[12.5px] font-bold leading-tight text-zinc-900">
            <Title text={title} hint={hint} />
          </span>
          {desc && (
            <span className="mt-0.5 block text-[11.5px] leading-snug text-zinc-500">
              {desc}
            </span>
          )}
        </span>
        {locked ? (
          <span
            className={cn(
              'text-[11px] font-bold',
              on ? 'text-accent' : 'text-zinc-400'
            )}
          >
            {on ? t('yes') : t('no')}
          </span>
        ) : (
          <Switch size="small" checked={on} className="pointer-events-none" />
        )}
      </div>
      {children && <div className="px-2.5 pb-2.5">{children}</div>}
    </div>
  );
}

/** A choice field: a value chip when read-only, a compact control otherwise. */
export function SettingChoice({
  value,
  onChange,
  locked,
  options,
  select,
  icon,
  title,
  hint,
}) {
  const label = options.find((o) => o.value === value)?.label ?? '—';
  const control = select ? (
    <Select
      size="small"
      value={value}
      onChange={onChange}
      disabled={locked}
      options={options}
      className="min-w-[230px]"
      popupMatchSelectWidth={false}
    />
  ) : (
    <Segmented
      size="small"
      value={value}
      onChange={onChange}
      disabled={locked}
      options={options}
    />
  );

  return (
    <div className="setting-card col-span-2 flex items-center gap-2.5 rounded-xl border border-zinc-200 bg-white p-2.5">
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-[rgba(var(--accentColor),0.1)] text-[15px] text-accent">
        {icon}
      </span>
      <span className="min-w-0 flex-1 text-[12.5px] font-bold text-zinc-900">
        <Title text={title} hint={hint} />
      </span>
      {locked ? (
        <span className="rounded-full bg-zinc-100 px-2.5 py-0.5 text-xs font-bold text-zinc-800">
          {label}
        </span>
      ) : (
        control
      )}
    </div>
  );
}

// Each setting is <Form.Item noStyle><SettingX .../></Form.Item>; choices are
// always full width. A half-width card left alone before a full-width one (or
// at the end) is stretched, so the two-column grid has no holes.
const innerOf = (el) => (isValidElement(el) ? el.props.children : null);
// (A flag on the component, not its identity: hot reload swaps functions.)
const isWide = (el) =>
  !!el?.type?.fullWidth ||
  !!innerOf(el)?.type?.fullWidth ||
  !!innerOf(el)?.props?.wide;
const stretch = (el) =>
  cloneElement(el, {}, cloneElement(innerOf(el), { wide: true }));

function withoutHoles(children) {
  const list = Children.toArray(children).filter(isValidElement);
  let run = 0;
  return list.map((el, i) => {
    if (isWide(el)) {
      run = 0;
      return el;
    }
    run += 1;
    const next = list[i + 1];
    const runEnds = !next || isWide(next);
    return runEnds && run % 2 === 1 ? stretch(el) : el;
  });
}

export function Group({ title, children }) {
  return (
    <section>
      {title && (
        <h3 className="mb-2 text-[11px] font-extrabold uppercase tracking-[0.08em] text-zinc-400">
          {title}
        </h3>
      )}
      <div className="grid grid-cols-2 gap-2">{withoutHoles(children)}</div>
    </section>
  );
}

/** A number field (e.g. max hours per day): a value chip when read-only. */
export function SettingNumber({
  value,
  onChange,
  locked,
  icon,
  title,
  hint,
  min,
  max,
}) {
  const control = locked ? (
    <span className="rounded-full bg-zinc-100 px-2.5 py-0.5 text-xs font-bold text-zinc-800">
      {value ?? '—'}
    </span>
  ) : (
    <InputNumber
      size="small"
      min={min}
      max={max}
      value={value}
      onChange={onChange}
      className="w-20"
    />
  );
  return (
    <div className="setting-card col-span-2 flex items-center gap-2.5 rounded-xl border border-zinc-200 bg-white p-2.5">
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-[rgba(var(--accentColor),0.1)] text-[15px] text-accent">
        {icon}
      </span>
      <span className="min-w-0 flex-1 text-[12.5px] font-bold text-zinc-900">
        <Title text={title} hint={hint} />
      </span>
      {control}
    </div>
  );
}

/** A select inside a card; a value chip when read-only. */
export function ValueSelect({
  value,
  onChange,
  options,
  locked,
  className = 'w-32',
  ...props
}) {
  if (locked) {
    return (
      <span className="rounded-full bg-zinc-100 px-2.5 py-0.5 text-xs font-bold text-zinc-800">
        {options?.find((o) => o.value === value)?.label ?? '—'}
      </span>
    );
  }
  return (
    <Select
      size="small"
      value={value}
      onChange={onChange}
      options={options}
      className={className}
      {...props}
    />
  );
}

// Currency value → flag file under /assets/icons/flags.
const CURRENCY_FLAGS = {
  rsd: 'rs',
  eur: 'eu',
  usd: 'us',
  gbp: 'gb',
  rub: 'ru',
};

const CurrencyLabel = ({ value, label }) => (
  <span className="inline-flex items-center gap-1.5">
    {CURRENCY_FLAGS[value] && (
      <img
        src={`/assets/icons/flags/${CURRENCY_FLAGS[value]}.svg`}
        alt=""
        className="h-3 w-5 object-contain"
      />
    )}
    {label}
  </span>
);

/**
 * ValueSelect for a currency: the flag next to the code, as the currency
 * badge shows it elsewhere.
 */
export function CurrencySelect({ value, options, locked, ...props }) {
  if (locked) {
    const label = options?.find((o) => o.value === value)?.label;
    return label ? (
      <CurrencyBadge
        className="mt-0 h-6"
        currency={label}
        currencyCode={CURRENCY_FLAGS[value]}
      />
    ) : (
      <span className="text-xs font-bold text-zinc-400">—</span>
    );
  }
  return (
    <ValueSelect
      value={value}
      options={options?.map((o) => ({
        ...o,
        label: <CurrencyLabel value={o.value} label={o.label} />,
      }))}
      {...props}
    />
  );
}

/** A small on/off pill (e.g. "Document" / "Email" on a document card). */
export function ChipToggle({ value, onChange, icon, label, disabled }) {
  const on = !!value;
  return (
    <button
      type="button"
      aria-pressed={on}
      disabled={disabled}
      onClick={() => onChange?.(!on)}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11.5px] font-semibold transition-colors',
        on
          ? 'border-[rgba(var(--accentColor),0.35)] bg-[rgba(var(--accentColor),0.1)] text-accent'
          : 'border-zinc-200 bg-white text-zinc-400',
        !disabled && 'cursor-pointer hover:border-accent'
      )}
    >
      {icon}
      {label}
    </button>
  );
}

/**
 * A full-width card around any control (a multi-select, a list): title on
 * top, the control below. Unlike the setting cards it wraps the Form.Item.
 */
export function FieldCard({ icon, title, hint, children }) {
  return (
    <div className="setting-card col-span-2 rounded-xl border border-zinc-200 bg-white p-2.5">
      <div className="mb-2 flex items-center gap-2.5">
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-[rgba(var(--accentColor),0.1)] text-[15px] text-accent">
          {icon}
        </span>
        <span className="min-w-0 flex-1 text-[12.5px] font-bold text-zinc-900">
          <Title text={title} hint={hint} />
        </span>
      </div>
      {children}
    </div>
  );
}

/** A module's main switch, as a banner (coloured while it is on). */
export function MasterSwitch({
  value,
  onChange,
  locked,
  icon = <TeamOutlined />,
  title,
  desc,
  summary,
}) {
  const { t } = useTranslation(['forms', 'translations']);
  const on = !!value;
  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-2xl p-3.5',
        on ? 'bg-accent text-white' : 'border border-zinc-200 bg-zinc-50'
      )}
    >
      <span
        className={cn(
          'grid h-10 w-10 shrink-0 place-items-center rounded-xl text-lg',
          on ? 'bg-white/15' : 'bg-zinc-200 text-zinc-500'
        )}
      >
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[15px] font-extrabold">{title}</div>
        <div className={cn('text-xs', on ? 'text-white/80' : 'text-zinc-500')}>
          {desc}
        </div>
        {on && summary && (
          <span className="mt-1.5 inline-block rounded-full bg-white/15 px-2.5 py-0.5 text-[11.5px] font-semibold">
            {summary}
          </span>
        )}
      </div>
      {locked ? (
        <span
          className={cn(
            'rounded-full px-3 py-1 text-xs font-extrabold first-letter:uppercase',
            on ? 'bg-white text-accent' : 'bg-zinc-200 text-zinc-500'
          )}
        >
          {t(on ? 'companyProfile.on' : 'companyProfile.off', {
            ns: 'translations',
          })}
        </span>
      ) : (
        <Switch
          checked={on}
          onChange={onChange}
          // On the green banner the checked track gets lost.
          className={cn(on && '!bg-white/35')}
        />
      )}
    </div>
  );
}

SettingChoice.fullWidth = true;
SettingNumber.fullWidth = true;
FieldCard.fullWidth = true;

/** A headline figure (a balance, a total); `accent` highlights the main one. */
export function StatFigure({ label, hint, value, loading, accent }) {
  return (
    <div
      className={cn(
        'rounded-xl border p-3',
        accent
          ? 'border-[rgba(var(--accentColor),0.35)] bg-[rgba(var(--accentColor),0.08)] text-accent'
          : 'border-zinc-200 bg-white'
      )}
    >
      <div className="text-[11.5px] font-semibold leading-tight text-zinc-500">
        {label}
        {hint && (
          <Tooltip title={hint}>
            <InfoCircleOutlined className="ml-1 text-[11px]" />
          </Tooltip>
        )}
      </div>
      <div className="mt-1 text-[26px] font-extrabold leading-none">
        {loading ? <Spin size="small" /> : (value ?? '~')}
      </div>
    </div>
  );
}
